import React from 'react';
import { isValidCpf, maskCPF } from '../utils/formatters';
import { IPCOM_SIGNERS, SignerForm, signatarioVazio } from '../utils/signatarios';
import { campo, campoComEstado } from '../design';

/**
 * QUEM ASSINA O DOCUMENTO.
 *
 * Saiu de dentro do modal de assinatura do CRM para servir aos dois fluxos do
 * mesmo `digital_contracts`: venda nova (lead → proposta) e termo aditivo
 * (contrato). Duas cópias divergiriam no dia em que a ClickSign mudasse uma
 * exigência — e a que não fosse atualizada barraria depois, do lado de lá, com
 * mensagem que não diz qual signatário é.
 *
 * O estado é do PAI: aqui não há fetch nem envio. Quem sabe qual documento está
 * sendo assinado é quem chamou.
 */

interface Props {
  signers: SignerForm[];
  onChange: (signers: SignerForm[]) => void;
  disabled?: boolean;
}

export default function EditorDeSignatarios({ signers, onChange, disabled = false }: Props) {
  const add = () => onChange([...signers, signatarioVazio()]);

  const addPreset = (key: 'heitor' | 'fabio') => {
    const preset = IPCOM_SIGNERS[key];
    // Já está na lista: não duplica. Clicar duas vezes é mais comum que parece,
    // e signatário repetido vira dois convites para a mesma pessoa.
    if (signers.some((s) => s.email === preset.email)) return;
    onChange([...signers, { ...preset, _expanded: false }]);
  };

  const toggle = (i: number) =>
    onChange(signers.map((s, idx) => (idx === i ? { ...s, _expanded: !s._expanded } : s)));

  const remove = (i: number) => onChange(signers.filter((_, idx) => idx !== i));

  const update = (i: number, campo: keyof SignerForm, valor: string) =>
    onChange(signers.map((s, idx) => (idx === i ? { ...s, [campo]: valor } : s)));

  return (
    <div>
      <div className='flex justify-between items-center mb-3 flex-wrap gap-2'>
        <h3 className='font-semibold text-white'>Signatários ({signers.length})</h3>
        <div className='flex items-center gap-2'>
          <button type='button' disabled={disabled} onClick={() => addPreset('heitor')} className='px-2.5 py-1.5 bg-amber-500/20 text-amber-300 rounded-lg text-xs hover:bg-amber-500/30 font-medium border border-amber-500/30 disabled:opacity-50'>+ Heitor</button>
          <button type='button' disabled={disabled} onClick={() => addPreset('fabio')} className='px-2.5 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg text-xs hover:bg-blue-500/30 font-medium border border-blue-500/30 disabled:opacity-50'>+ Fábio</button>
          <button type='button' disabled={disabled} onClick={add} className='px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 font-medium disabled:opacity-50'>+ Adicionar</button>
        </div>
      </div>
      <div className='space-y-2'>
        {signers.map((signer, idx) => {
          const cpfInvalid = !!signer.cpf && !isValidCpf(signer.cpf);
          const isWitness = signer.role === 'witness';
          const methodLabel = signer.authType === 'whatsapp' ? 'WhatsApp' : signer.authType === 'icp_brasil' ? 'Certificado' : 'E-mail';
          return (
            <div key={idx} className={`bg-slate-800 rounded-lg border border-slate-700 border-l-4 ${isWitness ? 'border-l-amber-500' : 'border-l-blue-500'}`}>
              <div className='flex items-center justify-between gap-2 p-3 cursor-pointer' onClick={() => toggle(idx)}>
                <div className='flex items-center gap-2 min-w-0'>
                  <span className='text-xs text-slate-500 w-4 shrink-0'>{idx + 1}</span>
                  <span className='font-medium text-white truncate'>{signer.name || 'Novo signatário'}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${isWitness ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}`}>{isWitness ? 'Testemunha' : 'Parte'}</span>
                  <span className='text-[10px] text-slate-400 shrink-0 hidden sm:inline'>{methodLabel}</span>
                  {cpfInvalid && <span className='text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 shrink-0'>CPF inválido</span>}
                </div>
                <div className='flex items-center gap-2 shrink-0'>
                  {signers.length > 1 && !disabled && (
                    <button type='button' onClick={(e) => { e.stopPropagation(); remove(idx); }} className='text-red-400 hover:bg-red-500/10 px-2 py-1 rounded text-xs'>Remover</button>
                  )}
                  <span className='text-slate-400 text-xs'>{signer._expanded ? '▾' : '▸'}</span>
                </div>
              </div>
              {signer._expanded && (
                <div className='grid grid-cols-2 gap-3 px-3 pb-3'>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>Nome *</label>
                    <input type='text' disabled={disabled} value={signer.name} onChange={(e) => update(idx, 'name', e.target.value)} placeholder='Nome completo' className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`} />
                  </div>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>Email *</label>
                    <input type='email' disabled={disabled} value={signer.email} onChange={(e) => update(idx, 'email', e.target.value)} placeholder='email@empresa.com' className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`} />
                  </div>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>CPF{signer.authType === 'icp_brasil' ? ' *' : ' (opcional p/ PJ)'}</label>
                    <input type='text' disabled={disabled} value={signer.cpf} onChange={(e) => update(idx, 'cpf', maskCPF(e.target.value))} placeholder='000.000.000-00' className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campoComEstado(cpfInvalid && 'border-red-500')}`} />
                    {cpfInvalid && <p className='text-[10px] text-red-400 mt-1'>CPF inválido</p>}
                  </div>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>Data Nascimento</label>
                    <input type='date' disabled={disabled} value={signer.birthday} onChange={(e) => update(idx, 'birthday', e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`} />
                  </div>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>Tipo</label>
                    <select disabled={disabled} value={signer.role} onChange={(e) => update(idx, 'role', e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`}>
                      <option value='sign'>Signatario (Parte)</option>
                      <option value='witness'>Testemunha</option>
                    </select>
                  </div>
                  <div>
                    <label className='block text-xs text-slate-500 mb-1'>Metodo de Assinatura</label>
                    <select disabled={disabled} value={signer.authType} onChange={(e) => update(idx, 'authType', e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`}>
                      <option value='email'>Codigo por Email</option>
                      <option value='whatsapp'>Codigo por WhatsApp</option>
                      <option value='icp_brasil'>Certificado Digital</option>
                    </select>
                  </div>
                  {signer.authType === 'whatsapp' && (
                    <div className='col-span-2'>
                      <label className='block text-xs text-slate-500 mb-1'>Telefone (WhatsApp) *</label>
                      <input type='text' disabled={disabled} value={signer.phone} onChange={(e) => update(idx, 'phone', e.target.value)} placeholder='45999999999' className={`w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60 ${campo}`} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
